import { z } from "zod";
import { BotLogEventSchema } from "./bot";
import { ActiveSpeakerNotifyEventSchema, ParticipantInWaitingRoomEventSchema, ParticipantJoinEventSchema, ParticipantLeaveEventSchema, } from "./call";
// Import status change schema from recall.ts
import { StatusChangeSchema, StatusCodeSchema, } from "./recall";
import { PartialTranscriptEventSchema, TranscriptDeletedEventSchema, TranscriptEventSchema, TranscriptProcessingEventSchema, } from "./transcript";
// Internal status change event schema - used for transforming real Recall events
export const StatusChangeEventSchema = z.object({
    event: z.literal("bot.status_change"),
    data: z.object({
        bot_id: z.string(),
        status: StatusChangeSchema,
    }),
});
// Bot done event schema (for bot.done events from Recall)
export const BotDoneEventSchema = z.object({
    event: z.literal("bot.done"),
    data: z.object({
        bot: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
        data: z.object({
            code: z.string(),
            sub_code: z.string().nullable(),
            updated_at: z.string(),
        }),
    }),
});
// Real Recall bot lifecycle event schemas (for parsing incoming webhooks)
// These will be transformed to StatusChangeEvent format internally
// Common bot event data structure from Recall's actual API
const RecallBotEventDataSchema = z.object({
    data: z.object({
        code: z.string(),
        sub_code: z.string().nullable(),
        updated_at: z.string(),
    }),
    bot: z.object({
        id: z.string(),
        metadata: z.record(z.unknown()),
    }),
});
// Individual Recall bot lifecycle event schemas
export const RecallBotJoiningCallEventSchema = z.object({
    event: z.literal("bot.joining_call"),
    data: RecallBotEventDataSchema,
});
export const RecallBotInWaitingRoomEventSchema = z.object({
    event: z.literal("bot.in_waiting_room"),
    data: RecallBotEventDataSchema,
});
// Note: bot.status_update is NOT an official Recall.ai event
// Recall.ai sends specific lifecycle events like bot.joining_call, bot.in_call_recording, etc.
export const RecallBotInCallNotRecordingEventSchema = z.object({
    event: z.literal("bot.in_call_not_recording"),
    data: RecallBotEventDataSchema,
});
export const RecallBotInCallRecordingEventSchema = z.object({
    event: z.literal("bot.in_call_recording"),
    data: RecallBotEventDataSchema,
});
export const RecallBotCallEndedEventSchema = z.object({
    event: z.literal("bot.call_ended"),
    data: RecallBotEventDataSchema,
});
export const RecallBotFatalEventSchema = z.object({
    event: z.literal("bot.fatal"),
    data: RecallBotEventDataSchema,
});
export const RecallBotRecordingPermissionAllowedEventSchema = z.object({
    event: z.literal("bot.recording_permission_allowed"),
    data: RecallBotEventDataSchema,
});
export const RecallBotRecordingPermissionDeniedEventSchema = z.object({
    event: z.literal("bot.recording_permission_denied"),
    data: RecallBotEventDataSchema,
});
// Recording lifecycle event schemas - these are crucial for call completion detection!
const RecallRecordingEventDataSchema = z.object({
    data: z.object({
        code: z.string(),
        sub_code: z.string().nullable(),
        updated_at: z.string(),
    }),
    recording: z.object({
        id: z.string(),
        metadata: z.record(z.unknown()),
    }),
    bot: z
        .object({
        id: z.string(),
        metadata: z.record(z.unknown()),
    })
        .nullable(),
});
const RecallTranscriptEventDataSchema = z.object({
    data: z.object({
        code: z.string(),
        sub_code: z.string().nullable(),
        updated_at: z.string(),
    }),
    transcript: z.object({
        id: z.string(),
        metadata: z.record(z.unknown()),
    }),
    recording: z.object({
        id: z.string(),
        metadata: z.record(z.unknown()),
    }),
    bot: z
        .object({
        id: z.string(),
        metadata: z.record(z.unknown()),
    })
        .nullable(),
});
export const RecallRecordingDoneEventSchema = z.object({
    event: z.literal("recording.done"),
    data: RecallRecordingEventDataSchema,
});
export const RecallRecordingFailedEventSchema = z.object({
    event: z.literal("recording.failed"),
    data: RecallRecordingEventDataSchema,
});
export const RecallTranscriptDoneEventSchema = z.object({
    event: z.literal("transcript.done"),
    data: RecallTranscriptEventDataSchema,
});
export const RecallTranscriptFailedEventSchema = z.object({
    event: z.literal("transcript.failed"),
    data: RecallTranscriptEventDataSchema,
});
// New participant event schemas for Recall.ai v1.11 API
export const RecallParticipantJoinEventSchema = z.object({
    event: z.literal("participant_events.join"),
    data: z.object({
        data: z.object({
            action: z.literal("join"),
            participant: z.object({
                id: z.number(),
                name: z.string().nullable(),
                is_host: z.boolean(),
                platform: z.string().nullable(),
                extra_data: z.union([z.record(z.unknown()), z.null()]).optional(),
            }),
            timestamp: z.object({
                relative: z.number(),
            }),
            data: z.null(),
        }),
        participant_events: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
        realtime_endpoint: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
        recording: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
        bot: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
    }),
});
export const RecallParticipantLeaveEventSchema = z.object({
    event: z.literal("participant_events.leave"),
    data: z.object({
        data: z.object({
            action: z.literal("leave"),
            participant: z.object({
                id: z.number(),
                name: z.string().nullable(),
                is_host: z.boolean(),
                platform: z.string().nullable(),
                extra_data: z.union([z.record(z.unknown()), z.null()]).optional(),
            }),
            timestamp: z.object({
                relative: z.number(),
            }),
            data: z.null(),
        }),
        participant_events: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
        realtime_endpoint: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
        recording: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
        bot: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
    }),
});
export const RecallParticipantUpdateEventSchema = z.object({
    event: z.literal("participant_events.update"),
    data: z.object({
        data: z.object({
            action: z.literal("update"),
            participant: z.object({
                id: z.number(),
                name: z.string().nullable(),
                is_host: z.boolean(),
                platform: z.string().nullable(),
                extra_data: z.union([z.record(z.unknown()), z.null()]).optional(),
            }),
            timestamp: z.object({
                relative: z.number(),
            }),
            data: z.null(),
        }),
        participant_events: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
        realtime_endpoint: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
        recording: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
        bot: z.object({
            id: z.string(),
            metadata: z.record(z.unknown()),
        }),
    }),
});
// Combined WebhookEvent - includes both original format and real Recall events
export const WebhookEventSchema = z.discriminatedUnion("event", [
    BotLogEventSchema,
    TranscriptEventSchema,
    PartialTranscriptEventSchema,
    TranscriptProcessingEventSchema,
    TranscriptDeletedEventSchema,
    // Include StatusChangeEventSchema for bot.status_change events from Recall
    StatusChangeEventSchema,
    // Include BotDoneEventSchema for bot.done events from Recall
    BotDoneEventSchema,
    // Real Recall bot lifecycle events
    RecallBotJoiningCallEventSchema,
    RecallBotInWaitingRoomEventSchema,
    RecallBotInCallNotRecordingEventSchema,
    RecallBotInCallRecordingEventSchema,
    RecallBotCallEndedEventSchema,
    RecallBotFatalEventSchema,
    RecallBotRecordingPermissionAllowedEventSchema,
    RecallBotRecordingPermissionDeniedEventSchema,
    // Real Recall recording/transcript lifecycle events - THESE ARE CRITICAL!
    RecallRecordingDoneEventSchema,
    RecallRecordingFailedEventSchema,
    RecallTranscriptDoneEventSchema,
    RecallTranscriptFailedEventSchema,
    // Legacy participant events (bot.participant_*)
    ParticipantJoinEventSchema,
    ParticipantLeaveEventSchema,
    ParticipantInWaitingRoomEventSchema,
    ActiveSpeakerNotifyEventSchema,
    // New participant events (participant_events.*)
    RecallParticipantJoinEventSchema,
    RecallParticipantLeaveEventSchema,
    RecallParticipantUpdateEventSchema,
]);
/**
 * Extract status data directly from a real Recall lifecycle event
 * No fake transformation - just extract the actual status information
 *
 * @throws {Error} If the webhook structure is invalid or missing required fields
 */
export function extractStatusFromRecallEvent(event) {
    // Validate the nested structure with Zod for runtime safety
    const ValidationSchema = z.object({
        data: z.object({
            data: z.object({
                code: z.string().min(1, "Status code cannot be empty"),
                sub_code: z.string().nullable(),
                updated_at: z.string().min(1, "Updated timestamp cannot be empty"),
            }),
            bot: z.object({
                id: z.string().min(1, "Bot ID cannot be empty"),
                metadata: z.record(z.unknown()),
            }),
        }),
    });
    const validationResult = ValidationSchema.safeParse(event);
    if (!validationResult.success) {
        throw new Error(`Invalid Recall webhook structure: ${validationResult.error.message}`);
    }
    const validatedData = validationResult.data;
    // Validate timestamp format
    const timestamp = validatedData.data.data.updated_at;
    if (!timestamp || Number.isNaN(Date.parse(timestamp))) {
        throw new Error(`Invalid timestamp format: ${timestamp}`);
    }
    // Validate and parse the status code
    const statusCode = StatusCodeSchema.parse(validatedData.data.data.code);
    // Extract the actual status data without fake transformation
    return {
        bot_id: validatedData.data.bot.id,
        status: {
            code: statusCode,
            message: null, // Always include message field, defaulting to null
            created_at: validatedData.data.data.updated_at,
            sub_code: validatedData.data.data.sub_code, // Always include sub_code (can be null)
        },
    };
}
/**
 * @deprecated Use extractStatusFromRecallEvent instead - this creates fake events
 * Legacy function for backward compatibility - transforms real events to fake format
 */
export function transformRecallLifecycleEventToStatusChange(event) {
    const extracted = extractStatusFromRecallEvent(event);
    return {
        event: "bot.status_change",
        data: extracted,
    };
}
/**
 * Check if a webhook event is a Recall bot lifecycle event
 */
export function isRecallBotLifecycleEvent(event) {
    return [
        "bot.joining_call",
        "bot.in_waiting_room",
        "bot.in_call_not_recording",
        "bot.in_call_recording",
        "bot.call_ended",
        "bot.fatal",
        "bot.recording_permission_allowed",
        "bot.recording_permission_denied",
    ].includes(event.event);
}
/**
 * Check if a webhook event is a Recall recording lifecycle event
 */
export function isRecallRecordingLifecycleEvent(event) {
    return [
        "recording.done",
        "recording.failed",
        "transcript.done",
        "transcript.failed",
    ].includes(event.event);
}
/**
 * Check if a webhook event indicates call completion (recording.done or transcript.done)
 */
export function isCallCompletionEvent(event) {
    return event.event === "recording.done" || event.event === "transcript.done";
}
/**
 * Extract bot ID from any Recall webhook event (bot or recording lifecycle)
 */
export function extractBotIdFromRecallEvent(event) {
    if (isRecallBotLifecycleEvent(event)) {
        return event.data.bot.id;
    }
    if (isRecallRecordingLifecycleEvent(event)) {
        return event.data.bot?.id ?? null;
    }
    return null;
}
// Internal request types
export const InternalRequestSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("SET_CALL_SCRIPT"),
        payload: z.any(), // CallScriptSchema - will be imported from schemas later
        isInternalCall: z.literal(true),
        timestamp: z.string().datetime({ precision: 3 }),
    }),
    z.object({
        type: z.literal("INIT"),
        payload: z.object({
            bot: z.any(), // RecallBotSchema - will be imported from recall.ts
            orgId: z.string(),
            callScript: z.any(), // CallScriptSchema - will be imported from schemas later
        }),
        isInternalCall: z.literal(true),
        timestamp: z.string().datetime({ precision: 3 }),
    }),
    z.object({
        type: z.literal("AI_SCRIPT_COMPLETION"),
        payload: z.object({
            scriptItemId: z.string(),
            completionDecision: z.object({
                completed: z.boolean(),
                confidence: z.number(),
            }),
        }),
        isInternalCall: z.literal(true),
        timestamp: z.string().datetime({ precision: 3 }),
    }),
    z.object({
        type: z.literal("REFRESH_PLAYBOOK"),
        payload: z
            .object({
            scriptId: z.string().optional(),
        })
            .optional(),
        isInternalCall: z.literal(true),
        timestamp: z.string().datetime({ precision: 3 }),
    }),
    z.object({
        type: z.literal("SWAP_SCRIPT"),
        payload: z.object({
            scriptId: z.string(),
        }),
        isInternalCall: z.literal(true),
        timestamp: z.string().datetime({ precision: 3 }),
    }),
]);
